/**
 * A service class for working with MapBox style-spec API in a more intuitive and convenient manner.
 *
 * This class provides utility functions to work with MapBox style-spec expressions, such as slicing strings, retrieving
 * attribute values, and performing operations like addition and comparison. It abstracts away some of the complexity
 * of working with style-spec directly and provides a more user-friendly interface.
 */
export class MapBoxStyleSpecService {
  /**
   * Slices the input string from the specified start index to the end index (if provided).
   * @param input The input string to slice.
   * @param startIndex The starting index (inclusive).
   * @param endIndex The ending index (exclusive). If not provided, the string will be sliced until the end.
   * @returns The style-spec expression for the sliced string.
   */
  static slice(
    input: string | Array<any>,
    startIndex: number | Array<any>,
    endIndex?: number | Array<any>
  ): Array<any> {
    const expression = ['slice', input, startIndex];
    if (endIndex) {
      expression[3] = endIndex;
    }
    return expression;
  }

  /**
   * Retrieves the value of an attribute from the properties of a Feature.
   * @param attributeName The name of the attribute to retrieve.
   * @returns The style-spec expression for retrieving the attribute value.
   */
  static getAttribute(attributeName: string): Array<any> {
    return ['get', attributeName];
  }

  /**
   * Searches for an element in a given string and returns its index. Returns -1 if not found.
   * @param searchElement The element to search for.
   * @param input The string to search in.
   * @returns The style-spec expression for finding the index of the search element.
   */
  static indexOf(
    searchElement: string | Array<any>,
    input: string | Array<any>
  ): Array<any> {
    return ['index-of', searchElement, input];
  }

  /**
   * Returns the length of a given string.
   * @param input The string to measure the length of.
   * @returns The style-spec expression for getting the length of the string.
   */
  static strLength(input: string | Array<any>): Array<any> {
    return ['length', input];
  }

  /**
   * Adds two operands and returns the result.
   * @param operand1 The first operand.
   * @param operand2 The second operand.
   * @returns The style-spec expression for adding the two operands.
   */
  static add(
    operand1: number | Array<any>,
    operand2: number | Array<any>
  ): Array<any> {
    return ['+', operand1, operand2];
  }

  /**
   * Compares two operands for inequality and returns a boolean result.
   * @param operand1 The first operand.
   * @param operand2 The second operand.
   * @returns The style-spec expression for checking if the two operands are not equal.
   */
  static notEqualTo(operand1: any, operand2: any): Array<any> {
    return ['!=', operand1, operand2];
  }

  /**
   * Slices the input string from the end of the specified start string.
   * @param inputString The string to slice.
   * @param startString The string to slice from the end of.
   * @returns The style-spec expression for slicing the string from the end of the start string.
   */
  static sliceFromEndOfString(
    inputString: string | Array<any>,
    startString: string | Array<any>
  ): Array<any> {
    const startIndex = this.add(
      this.indexOf(startString, inputString),
      this.strLength(startString)
    );
    return this.slice(inputString, startIndex);
  }

  /**
   * Slices the input string until the specified end string.
   * @param inputString The string to slice.
   * @param endString The string to slice until.
   * @returns The style-spec expression for slicing the string until the end string.
   */
  static sliceUntilString(
    inputString: string | Array<any>,
    endString: string | Array<any>
  ): Array<any> {
    const endIndex = this.indexOf(endString, inputString);
    return this.slice(inputString, 0, endIndex);
  }

  /**
   * Retrieves the value of an attribute from a composite object within the properties of a Feature.
   * @param attributeName The name of the attribute to retrieve.
   * @param compositeObjectKey The key of the composite object within the properties.
   * @param separator The separator used between the attribute name and its value.
   * @returns The style-spec expression for retrieving the attribute value from the composite object.
   */
  static getAttributeFromCompositeObject(
    attributeName: string,
    compositeObjectKey = 'layer_properties',
    separator = ':'
  ): (string | any[])[] {
    const attributeKey = `${attributeName}${separator}`;
    const slicedString = this.sliceFromEndOfString(
      this.getAttribute(compositeObjectKey),
      attributeKey
    );

    const hasComma = this.notEqualTo(this.indexOf(',', slicedString), -1);

    const valueWithComma = this.sliceUntilString(slicedString, ',');
    const valueWithoutComma = this.sliceUntilString(slicedString, '}');

    return ['case', hasComma, valueWithComma, valueWithoutComma];
  }
}
